import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { DOCUMENT, PlatformLocation } from '@angular/common';
import { Platform } from '@angular/cdk/platform';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { logger } from './core/utils/console';

import { FuseConfigService } from '@fuse/services/config.service';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';

import { navigation, paciente } from 'app/config/fuse-config/navigation/navigation';
import { locale as navigationEnglish } from 'app/config/fuse-config/navigation/i18n/en';
import { locale as navigationTurkish } from 'app/config/fuse-config/navigation/i18n/tr';
import { locale as navigationPortuguese } from 'app/config/fuse-config/navigation/i18n/pt';
import { NavigationStart, NavigationEnd, NavigationError, NavigationCancel, Router, RouterEvent } from '@angular/router';
import { APIGestaCardService } from './core/api/gestacard.service';
import { AuthService } from './core/auth/auth.service';

@Component({
    selector   : 'app',
    templateUrl: './app.component.html',
    styleUrls  : ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy
{
    fuseConfig: any;
    navigation: any;

    config: any = {
        // Color themes can be defined in src/app/app.theme.scss
        colorTheme      : 'theme-default',
        customScrollbars: true,
        layout          : {
            style    : 'vertical-layout-1',
            width    : 'fullwidth',
            navbar   : {
                primaryBackground  : 'fuse-white-500',
                secondaryBackground: 'fuse-navy-G60',
                folded             : false,
                hidden             : false,
                position           : 'left',
                variant            : 'vertical-style-1'
            },
            toolbar  : {
                customBackgroundColor: true,
                background           : 'fuse-navy-G10',
                hidden               : false,
                position             : 'below-static'
            },
            footer   : {
                customBackgroundColor: true,
                background           : 'fuse-navy-G10',
                hidden               : false,
                position             : 'below-fixed'
            },
            sidepanel: {
                hidden  : false,
                position: 'right'
            }
        }
    };

    // Private
    private _unsubscribeAll: Subject<any>;

    /**
     * Constructor
     *
     * @param {DOCUMENT} document
     * @param {FuseConfigService} _fuseConfigService
     * @param {FuseNavigationService} _fuseNavigationService
     * @param {FuseSidebarService} _fuseSidebarService
     * @param {FuseSplashScreenService} _fuseSplashScreenService
     * @param {FuseTranslationLoaderService} _fuseTranslationLoaderService
     * @param {Platform} _platform
     * @param {TranslateService} _translateService
     */
    constructor(
        @Inject(DOCUMENT) private document: any,
        private _fuseConfigService: FuseConfigService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseSidebarService: FuseSidebarService,
        private _fuseSplashScreenService: FuseSplashScreenService,
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
        private _translateService: TranslateService,
        private _platform: Platform, 
        private location: PlatformLocation,
        private router: Router,
        private _apigestacard: APIGestaCardService,
        private _authService: AuthService
    )
    {

        location.onPopState((event) => {

            logger.log('onPopState', event);

        });

        // Get default navigation
        this.navigation = navigation;

        // Carrega o menu após o login ser concluído
        // Para login via key, o menu precisa ser carregado depois do token ser salvo
        this._authService.loginCompleted.subscribe(() => {
            logger.log('[AppComponent] loginCompleted event received, loading menu...');
            
            this._apigestacard.GetMenuOptionsObs().pipe(take(1)).subscribe((menu) => {
                logger.log('[AppComponent] Menu loaded:', menu.id);
                
                // Sempre registra e configura o menu (força atualização)
                this._fuseNavigationService.unregister(menu.id);
                this._fuseNavigationService.register(menu.id, [menu, this.navigation]);
                this._fuseNavigationService.setCurrentNavigation(menu.id || 'menu');
                
            }, (error) => {
                logger.error('[AppComponent] Erro ao carregar menu após login:', error);
            });
        });
        
        // Add languages
        this._translateService.addLangs(['pt', 'en']);

        // Set the default language
        this._translateService.setDefaultLang('pt');

        // Set the navigation translations
        this._fuseTranslationLoaderService.loadTranslations(navigationPortuguese, navigationEnglish);

        // Use a language
        this._translateService.use('pt');

        /**
         * ----------------------------------------------------------------------------------------------------
         * ngxTranslate Fix Start
         * ----------------------------------------------------------------------------------------------------
         */

        /**
         * If you are using a language other than the default one, i.e. Turkish in this case,
         * you may encounter an issue where some of the components are not actually being
         * translated when your app first initialized.
         *
         * This is related to ngxTranslate module and below there is a temporary fix while we
         * are moving the multi language implementation over to the Angular's core language
         * service.
         **/

        // Set the default language to 'en' and then back to 'tr'.
        // '.use' cannot be used here as ngxTranslate won't switch to a language that's already
        // been selected and there is no way to force it, so we overcome the issue by switching
        // the default language back and forth.
        /**
         setTimeout(() => {
            this._translateService.setDefaultLang('en');
            this._translateService.setDefaultLang('tr');
         });
         */

        /**
         * ----------------------------------------------------------------------------------------------------
         * ngxTranslate Fix End
         * ----------------------------------------------------------------------------------------------------
         */

        // Add is-mobile class to the body if the platform is mobile
        if ( this._platform.ANDROID || this._platform.IOS )
        {
            this.document.body.classList.add('is-mobile');
        }

        // Set the private defaults
        this._unsubscribeAll = new Subject();

        // Timeout de segurança: garante que o splash seja removido após 8 segundos
        // mesmo que algo dê errado no processo de inicialização
        setTimeout(() => {
            logger.log('[AppComponent] Timeout de segurança: removendo splash screen');
            this._fuseSplashScreenService.hide();
        }, 8000);

    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {

        // window.setInterval(() => {
        //   if (localStorage.getItem('expiresIn') && localStorage.getItem('refreshToken')) {
        //     if (new Date().getTime() + (5 * 60 * 1000) > parseInt(localStorage.getItem('expiresIn'))) {
        //         this._apigestacard.GetNewAccessToken().then(() => {
        //             window.location.reload()
        //         })
        //     } else {
        //         let dias = Math.floor((parseInt(localStorage.getItem('expiresIn')) - new Date().getTime()) / (1000 * 60 * 60 * 24))
        //         let horas = Math.floor((parseInt(localStorage.getItem('expiresIn')) - new Date().getTime()) / (1000 * 60 * 60) % 24)
        //         let minutos = Math.floor((parseInt(localStorage.getItem('expiresIn')) - new Date().getTime()) / (1000 * 60) % 60)
        //         let segundos = Math.floor((parseInt(localStorage.getItem('expiresIn')) - new Date().getTime()) / 1000 % 60)

        //         // Debug de tempo restante para o token expirar
        //         // console.log('Token válido por mais', dias, 'dias', horas, 'horas', minutos, 'minutos', segundos, 'segundos')
        //     }
        //   }
        // }, 1000)
        
        // Subscribe to config changes
        this._fuseConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config) => {

                this.fuseConfig = config;

                // Boxed
                if ( this.fuseConfig.layout.width === 'boxed' )
                {
                    this.document.body.classList.add('boxed');
                }
                else
                {
                    this.document.body.classList.remove('boxed');
                }

                // Color theme - Use normal for loop for IE11 compatibility
                for ( let i = 0; i < this.document.body.classList.length; i++ )
                {
                    const className = this.document.body.classList[i];

                    if ( className.startsWith('theme-') )
                    {
                        this.document.body.classList.remove(className);
                    }
                }

                this.document.body.classList.add(this.fuseConfig.colorTheme);
            });

            this.router.events
            .pipe(filter((event: RouterEvent) => (event instanceof NavigationStart)))
            .subscribe(( event: NavigationStart ) => {
    
                logger.group( 'NavigationStart Event', event.url, event );

            
              // Every navigation sequence is given a unique ID. Even "popstate"
              // navigations are really just "roll forward" navigations that get
              // a new, unique ID.
              // The "navigationTrigger" will be one of:
              // --
              // - imperative (ie, user clicked a link).
              // - popstate (ie, browser controlled change such as Back button).
              // - hashchange
              // --
              // NOTE: I am not sure what triggers the "hashchange" type.
    
              // This "restoredState" property is defined when the navigation
              // event is triggered by a "popstate" event (ex, back / forward
              // buttons). It will contain the ID of the earlier navigation event
              // to which the browser is returning.
              // --
              // CAUTION: This ID may not be part of the current page rendering.
              // This value is pulled out of the browser; and, may exist across
              // page refreshes.
              if ( event.restoredState ) {
                // console.log('restoring navigation id:', event.restoredState.navigationId);
                this.router.getCurrentNavigation().extras.state = event.restoredState;
              }
              logger.groupEnd();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void
    {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }
}
